import React, { useRef, forwardRef, useState } from "react";
import { Typography, Grid, IconButton } from "@mui/material";
import CropIcon from "@mui/icons-material/Crop";
import RefreshIcon from "@mui/icons-material/Refresh";
import { styled } from "@mui/material/styles";
import Cropper from "react-cropper";
import get from "lodash/get";
import "cropperjs/dist/cropper.css";
import MarketingFlyerModalBodyContainer from "./MarketingFlyerModalBodyContainer";

const GenerateImageEditorModal = (props) => {
    const {
        reportName,
        editorDimensions,
        modalContent,
        setMarketingFlyerPhoto,
        resetPhoto,
        handleCloseModal,
        timeOnMarketInput,
        setDealParty,
        compTab,
        generateCompReport,
        selectedCompId,
    } = props;

    const { dealParty, timeOnMarket, marketingFlyerPhoto, marketingFlyerPhotos } = modalContent;
    const cropperRef = useRef(null);

    const [selectedMarketingFlyerPhotoSrcUrl, setSelectedMarketingFlyerPhotoSrcUrl] = useState("");
    const [originalSelectedMarketingFlyerPhotoSrcUrl, setOriginalSelectedMarketingFlyerPhotoSrcUrl] = useState("");

    const getCropData = () => {
        if (typeof cropperRef.current.cropper !== "undefined") {
            const dataUrl = cropperRef.current.cropper.getCroppedCanvas().toDataURL();
            setSelectedMarketingFlyerPhotoSrcUrl(dataUrl);

            // Fallback logic for Comp Photo
            setMarketingFlyerPhoto(dataUrl);
        }
    };

    const handleGenerateMarketingFlyerReport = () => {
        getCropData();
        const croppedPhoto = cropperRef.current.cropper.getCroppedCanvas().toDataURL();
        const croppedPhotoSlice = croppedPhoto.split(",")[1];
        generateCompReport(compTab, false, null, "MarketingFlyer", null, dealParty, timeOnMarket, croppedPhotoSlice);
    };

    const handleSelectPhotoFromDropdown = (selectedPhotoId) => {
        const selectedPhoto = marketingFlyerPhotos.find((photo) => photo.mediaAssetKey === selectedPhotoId);
        setOriginalSelectedMarketingFlyerPhotoSrcUrl(get(selectedPhoto, "url", ""));
        setSelectedMarketingFlyerPhotoSrcUrl(get(selectedPhoto, "url", ""));
    };

    const handleResetPhoto = () => {
        setSelectedMarketingFlyerPhotoSrcUrl(originalSelectedMarketingFlyerPhotoSrcUrl);

        // Fallback logic for Comp Photo
        resetPhoto();
    };

    // Fallback logic for Comp Photo
    const shouldShowPreview =
        marketingFlyerPhotos.length === 0
            ? marketingFlyerPhoto.length > 0
            : selectedMarketingFlyerPhotoSrcUrl.length > 0;
    const shouldDisableButtons =
        marketingFlyerPhotos.length === 0
            ? marketingFlyerPhoto.length === 0
            : selectedMarketingFlyerPhotoSrcUrl.length === 0;

    return (
        <React.Fragment>
            <Paper container spacing={8} justifyContent="center">
                <CenterContent item xs={12}>
                    <EditorView
                        editorDimensions={editorDimensions}
                        marketingFlyerPhoto={marketingFlyerPhoto}
                        marketingFlyerPhotos={marketingFlyerPhotos}
                        selectedMarketingFlyerPhotoSrcUrl={selectedMarketingFlyerPhotoSrcUrl}
                        handleResetPhoto={handleResetPhoto}
                        ref={cropperRef}
                        getCropData={getCropData}
                        shouldDisableButtons={shouldDisableButtons}
                    />
                </CenterContent>
                <CenterContent item xs={12}>
                    <Grid container>
                        <Grid
                            item
                            xs={9}
                            container
                            style={{ padding: "5px", paddingRight: "15px", alignContent: "space-between" }}
                        >
                            <Grid item xs={12}>
                                <ModalHeader reportName={reportName} />
                            </Grid>
                            <MarketingFlyerModalBodyContainer
                                handleCloseModal={handleCloseModal}
                                handleGenerateMarketingFlyerReport={handleGenerateMarketingFlyerReport}
                                handleSelectPhotoFromDropdown={handleSelectPhotoFromDropdown}
                                modalContent={modalContent}
                                setDealParty={setDealParty}
                                timeOnMarketInput={timeOnMarketInput}
                                selectedCompId={selectedCompId}
                                shouldDisableButtons={shouldDisableButtons}
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Typography
                                gutterBottom={true}
                                style={{ fontWeight: "bold", marginBottom: "25px" }}
                                variant="h6"
                            >{`Preview`}</Typography>
                            {shouldShowPreview ? <Preview /> : null}
                        </Grid>
                    </Grid>
                </CenterContent>
            </Paper>
        </React.Fragment>
    );
};

const EditorView = forwardRef((props, ref) => {
    const {
        editorDimensions,
        marketingFlyerPhoto,
        marketingFlyerPhotos,
        selectedMarketingFlyerPhotoSrcUrl,
        handleResetPhoto,
        getCropData,
        shouldDisableButtons,
    } = props;

    return (
        <React.Fragment>
            <EditorViewStyle style={{ height: editorDimensions.height, width: editorDimensions.width }}>
                <EditorButtons>
                    <IconButton disabled={shouldDisableButtons} color="primary">
                        <CropIcon onClick={() => getCropData()} />
                    </IconButton>
                    <IconButton disabled={shouldDisableButtons} color="primary">
                        <RefreshIcon onClick={handleResetPhoto} />
                    </IconButton>
                </EditorButtons>
                <Cropper
                    ref={ref}
                    style={{ height: "100%", width: "100%" }}
                    zoomTo={0.5}
                    initialAspectRatio={153 / 55}
                    preview=".img-preview"
                    src={marketingFlyerPhotos.length === 0 ? marketingFlyerPhoto : selectedMarketingFlyerPhotoSrcUrl}
                    viewMode={0}
                    minCropBoxHeight={10}
                    minCropBoxWidth={10}
                    minCanvasWidth={1224}
                    minCanvasHeight={440}
                    cropBoxResizable={false}
                    cropBoxMovable={false}
                    dragMode={"move"}
                    background={false}
                    responsive={true}
                    autoCropArea={1}
                    toggleDragModeOnDblclick={false}
                    checkOrientation={false} // https://github.com/fengyuanchen/cropperjs/issues/671
                />
            </EditorViewStyle>
        </React.Fragment>
    );
});

const ModalHeader = (props) => {
    return (
        <React.Fragment>
            <Typography
                gutterBottom={true}
                style={{ fontWeight: "bold", marginBottom: "25px" }}
                variant="h6"
            >{`Create A ${props.reportName} Report`}</Typography>
        </React.Fragment>
    );
};

const Preview = (props) => {
    return (
        <React.Fragment>
            <PreviewStyle>
                <ImgPreview className="img-preview"></ImgPreview>
            </PreviewStyle>
        </React.Fragment>
    );
};

const Paper = styled(Grid)(({ theme }) => ({
    display: "flex",
    background: "white",
    top: "50%",
    left: "50%",
    width: "1248px",
    height: "auto",
    transform: "translate(-50%, -50%)",
    position: "absolute",
    borderRadius: "3px",
    alignContent: "center",
    boxShadow: " 0 3px 7px rgba(0, 0, 0, 0.3)",
    padding: "5px",
}));

const EditorViewStyle = styled("div")(({ theme }) => ({
    display: "flex",
    border: "1px solid black",
    borderRadius: "3px",
    position: "relative",
    justifyContent: "center",
}));

const EditorButtons = styled("span")(({ theme }) => ({
    alignSelf: "flex-end",
    position: "absolute",
    bottom: "0%",
    right: "0%",
    zIndex: "100",
}));

const PreviewStyle = styled("div")(({ theme }) => ({
    display: "flex",
    height: "100%",
    width: "100%",
    overflow: "hidden",
}));

const CenterContent = styled(Grid)(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
}));

const ImgPreview = styled("div")(({ theme }) => ({
    border: "1px solid black",
    overflow: "hidden",
    borderRadius: "3px",
    width: "100%",
}));

export default GenerateImageEditorModal;
